// Shopify theme sync marker: listing-rewrite-2026-06-21.
import { createSoAtlantaClient, normalizeBusinesses, readJsonScript } from '@theme/so-atlanta-api';
import { venueProfileUrl } from '@theme/so-atlanta-urls';

const PAGE_SIZE_MAX = 100;
const RANDOM_CATEGORY_VALUE = '__random_category__';

class SoAtlantaDropMeOff extends HTMLElement {
  settings = {};
  client = null;
  businesses = [];
  categories = [];

  connectedCallback() {
    this.settings = readJsonScript(this, '[data-sa-drop-me-off-settings]');
    this.client = createSoAtlantaClient(this.settings);
    this.#bindForm();
    void this.#loadBusinesses();
  }

  #bindForm() {
    const form = this.#form;
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#openRandomBusiness();
    });
  }

  async #loadBusinesses() {
    this.#setBusy(true);
    this.#setStatus('');

    try {
      this.businesses = await this.#fetchAllBusinesses();
      this.categories = this.#categoryOptions(this.businesses);
      this.#populateCategories();

      if (!this.businesses.length) {
        this.#setStatus(this.settings.emptyText || 'No businesses are available right now.');
        return;
      }

      this.#setBusy(false);
    } catch (error) {
      console.error('So Atlanta drop me off failed', error);
      this.#setStatus(this.settings.errorText || 'Unable to load the directory right now.');
    }
  }

  async #fetchAllBusinesses() {
    if (!this.client) return [];

    const limit = Math.min(Math.max(Number(this.settings.limit || PAGE_SIZE_MAX), 10), PAGE_SIZE_MAX);
    let offset = 0;
    let total = Infinity;
    const all = [];
    const seen = new Set();

    while (offset < total) {
      const data = await this.client.listings({ limit, offset, sort: 'featured', type: 'PLACE' });
      const page = normalizeBusinesses(data?.listings || data?.businesses)
        .filter((listing) => listing.type === 'PLACE' && listing.dropMeOffEligible);
      total = Number(data?.pagination?.total || all.length + page.length);

      for (const business of page) {
        const key = business.slug || business.shopifyMetaobjectHandle || business.name;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        all.push(business);
      }

      if (!page.length) break;
      offset += Number(data?.pagination?.limit || limit);
    }

    return all;
  }

  #populateCategories() {
    const select = this.#select;
    if (!(select instanceof HTMLSelectElement)) return;

    select.innerHTML = [
      `<option value="">${this.#escapeHtml(this.settings.allCategoriesLabel || 'All categories')}</option>`,
      `<option value="${RANDOM_CATEGORY_VALUE}">${this.#escapeHtml(
        this.settings.randomCategoryLabel || 'Random category'
      )}</option>`,
      ...this.categories.map((category) => `<option value="${this.#escapeAttr(category)}">${this.#escapeHtml(category)}</option>`),
    ].join('');
  }

  #openRandomBusiness() {
    const business = this.#randomBusiness();
    if (!business) {
      this.#setStatus(this.settings.emptyText || 'No businesses are available right now.');
      return;
    }

    const href = venueProfileUrl(business, this.settings.venueDirectoryPath);
    if (!href) {
      this.#setStatus('This business does not have a profile link yet.');
      return;
    }

    window.location.assign(href);
  }

  #randomBusiness() {
    if (!this.businesses.length) return null;

    const selected = this.#select instanceof HTMLSelectElement ? this.#select.value : '';
    let category = selected;

    if (selected === RANDOM_CATEGORY_VALUE) {
      category = this.#randomItem(this.categories) || '';
    }

    const pool = category ? this.businesses.filter((business) => this.#hasCategory(business, category)) : this.businesses;
    return this.#randomItem(pool.length ? pool : this.businesses);
  }

  #categoryOptions(businesses) {
    const categories = new Set();

    for (const business of businesses) {
      for (const category of this.#categoryList(business)) {
        categories.add(category);
      }
    }

    return [...categories].sort((a, b) => a.localeCompare(b));
  }

  #hasCategory(business, selectedCategory) {
    const selected = String(selectedCategory || '').toLowerCase();
    return this.#categoryList(business).some((category) => category.toLowerCase() === selected);
  }

  #categoryList(business) {
    const categories = Array.isArray(business?.categories) ? business.categories : [];
    return [...new Set([...categories, business?.category].filter(Boolean).map((category) => String(category).trim()))];
  }

  #randomItem(items) {
    if (!items.length) return null;
    return items[Math.floor(Math.random() * items.length)];
  }

  #setBusy(isBusy) {
    const select = this.#select;
    const button = this.#button;

    if (select instanceof HTMLSelectElement) {
      select.disabled = isBusy || !this.businesses.length;
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = isBusy || !this.businesses.length;
    }
  }

  #setStatus(message) {
    const status = this.#status;
    if (status instanceof HTMLElement) status.textContent = message;
  }

  #escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #escapeAttr(value) {
    return this.#escapeHtml(value);
  }

  get #form() {
    return this.querySelector('[ref="dropMeOffForm"]');
  }

  get #select() {
    return this.querySelector('[ref="categorySelect"]');
  }

  get #button() {
    return this.querySelector('[ref="randomButton"]');
  }

  get #status() {
    return this.querySelector('[ref="status"]');
  }
}

if (!customElements.get('so-atlanta-drop-me-off')) {
  customElements.define('so-atlanta-drop-me-off', SoAtlantaDropMeOff);
}
